/**
 * Price framing for the paywall, pricing page and results offer.
 * Pure helpers: every number comes from constants (prices, exam windows, CFA Institute fees),
 * never from marketing guesses. No prices or plans are changed here.
 */
import { getUpcomingExamWindows, normalizeExamLevel, todayYmd } from '../constants/examWindows';
import { REFUND_GRACE_DAYS } from '../constants/pricing';
import { resultsMockCount } from './resultsOffer';
import { BILLING_EMAIL } from '../constants/contact';

/**
 * Same monthly-first rule EasyPay uses: lead with monthly when asked, unless a regional
 * sale flips presentation to lifetime-first (T3/T4).
 */
export function shouldLeadWithMonthly({
  leadWithMonthly = false,
  showMonthlyOption = true,
  lifetimeFirst = false,
} = {}) {
  return Boolean(leadWithMonthly && showMonthlyOption && !lifetimeFirst);
}

/**
 * CFA Institute registration fees (USD) for the 2026–27 cycle, per exam sitting.
 * Source: CFA Institute dates & fees page, summarized in /cfa-level-1-exam-fees/.
 * A retake is a new registration at the same fee.
 */
export const CFA_REGISTRATION_FEES_USD = {
  l1: { early: 1140, standard: 1490 },
  l2: { early: 1140, standard: 1490 },
  l3: { early: 1240, standard: 1590 },
};
export const REGISTRATION_FEE_POST_PATH = '/cfa-level-1-exam-fees/';

/** Monthly access is one billing period per payment; stay well inside the shortest month. */
const ONE_MONTH_DAYS = 28;

function ymdToUtcMs(ymd) {
  const [y, m, d] = String(ymd || '')
    .slice(0, 10)
    .split('-')
    .map(Number);
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d);
}

function daysBetween(fromYmd, toYmd) {
  const a = ymdToUtcMs(fromYmd);
  const b = ymdToUtcMs(toYmd);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return NaN;
  return Math.round((b - a) / 86400000);
}

function examDateYmd(value) {
  if (!value) return '';
  if (typeof value?.toDate === 'function') return todayYmd(value.toDate());
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : todayYmd(value);
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

/**
 * Days from today to the candidate's exam: their saved exam date when it is still ahead,
 * else the start of the next official window for the level. Null when neither is ahead.
 * @returns {{ days: number, label: string, source: 'profile'|'window' } | null}
 */
export function examRunway({ level = 'l1', examDate = '', now = new Date() } = {}) {
  const today = todayYmd(now);
  const saved = examDateYmd(examDate);
  if (saved) {
    const days = daysBetween(today, saved);
    if (Number.isFinite(days) && days > 0) {
      return { days, lastDays: days, label: 'your exam', source: 'profile' };
    }
  }
  const next = getUpcomingExamWindows(normalizeExamLevel(level), now).find(
    (w) => daysBetween(today, w.start) > 0
  );
  if (!next) return null;
  return {
    days: daysBetween(today, next.start),
    // Last day of the window: monthly only "covers it" if access lasts through the window end.
    lastDays: daysBetween(today, next.end),
    label: `the ${next.label} window`,
    source: 'window',
  };
}

function money(usd) {
  const n = Number(usd);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

function usdFee(n) {
  return `USD ${Number(n).toLocaleString('en-US')}`;
}

/**
 * Plain-language answers to "is it worth the price?" for one level.
 * @returns {{ runway: string, planFit: string, retake: string, retakeFeeUsd: number }}
 */
export function planValueLines({
  level = 'l1',
  lifetimeUsd,
  monthlyUsd,
  examDate = '',
  now = new Date(),
  /** Results page (CFA-8): our price appears only on the purchase buttons, so no $ amounts here. */
  priceFree = false,
} = {}) {
  const lv = normalizeExamLevel(level);
  const lifetime = Number(lifetimeUsd);
  const monthly = Number(monthlyUsd);
  const runway = examRunway({ level: lv, examDate, now });
  const fee = (CFA_REGISTRATION_FEES_USD[lv] || CFA_REGISTRATION_FEES_USD.l1).standard;
  const retake = `A retake is a new CFA Institute registration: ${usdFee(fee)} at the standard fee.`;
  const choose = 'Pick monthly for one sitting soon; pick lifetime if you may resit or sit a later window.';

  if (priceFree) {
    let runwayLine = '';
    if (runway && runway.lastDays <= ONE_MONTH_DAYS) {
      runwayLine = `${runway.days} day${runway.days === 1 ? '' : 's'} to ${runway.label}: one month of the monthly plan covers it. Cancel anytime; access runs to the end of the paid month.`;
    } else if (runway) {
      runwayLine = `${runway.days} days to ${runway.label}. Lifetime never expires, so it also covers a resit.`;
    }
    return { runway: runwayLine, planFit: choose, retake, retakeFeeUsd: fee };
  }

  let runwayLine = '';
  if (runway && Number.isFinite(monthly) && monthly > 0 && runway.lastDays <= ONE_MONTH_DAYS) {
    runwayLine = `${runway.days} day${runway.days === 1 ? '' : 's'} to ${runway.label}: one month at ${money(
      monthly
    )} covers it. Cancel anytime; access runs to the end of the paid month.`;
  } else if (runway && Number.isFinite(lifetime) && lifetime > 0) {
    const perDay = lifetime / runway.days;
    runwayLine = `${runway.days} days to ${runway.label}: lifetime at ${money(
      lifetime
    )} works out to about ${money(Math.ceil(perDay * 100) / 100)} a day, and it never expires.`;
  }

  let planFit = '';
  if (Number.isFinite(lifetime) && Number.isFinite(monthly) && monthly > 0 && lifetime > 0) {
    const months = Math.ceil(lifetime / monthly);
    const exact = months * monthly === lifetime;
    planFit = `Lifetime costs ${exact ? 'the same as' : 'less than'} ${months} month${
      months === 1 ? '' : 's'
    } of monthly. ${choose}`;
  }

  return { runway: runwayLine, planFit, retake, retakeFeeUsd: fee };
}

/** True trust facts only: Stripe checkout, the refund policy in Terms, renewal terms. */
export function checkoutTrustFacts() {
  return [
    'Secure Stripe checkout · card, or Apple Pay / Google Pay where available',
    // Mirrors Terms (Legal.js): a courtesy refund review, requested by email, before any paid use.
    `Refund review within ${REFUND_GRACE_DAYS} days if you have not started a paid mock or drill`,
    'Lifetime never renews · monthly cancels anytime',
  ];
}

export function missCountLabel(missed) {
  return `${missed} miss${missed === 1 ? '' : 'es'}`;
}

/** Guest results score line. `missed` counts auto-graded items only, like `correct` and `total`. */
export function guestMissesLine({ correct, total, missed }) {
  return `${correct}/${total} · ${missed > 0 ? `${missCountLabel(missed)} to review` : 'no misses'}`;
}

/** Guest price box headline. Empty when there are no misses to name. */
export function guestUnlockHeadline(missed, level = 'l1') {
  if (!(missed > 0)) return '';
  return `Unlock explanations for your ${missCountLabel(missed)} + Mock Exams 2–${resultsMockCount(level)}`;
}

/**
 * Counts for the results offer, without naming topics (the topic breakdown stays paid).
 * @param rows output of sessionTopicRows()
 */
export function resultTeaserCounts(rows = [], missed = 0) {
  const topicCount = rows.length;
  const weakCount = rows.filter((r) => r.needsWork).length;
  return { topicCount, weakCount, missed: Math.max(0, Number(missed) || 0) };
}

/** One sentence tied to this sitting. Empty when there is nothing graded to talk about. */
export function resultTeaserLine({ topicCount, weakCount, missed }, { level = 'l1', personalizedOffer = false } = {}) {
  if (!topicCount || topicCount < 2) return '';
  const lv = normalizeExamLevel(level);
  const mocks = personalizedOffer ? `Mock Exams 2–${resultsMockCount(lv)}` : lv === 'l1' ? 'Mocks 2–8' : 'Mocks 2–4';
  const misses = missCountLabel(missed);
  if (weakCount > 0) {
    const next = lv === 'l1' ? `then drill those topics and sit ${mocks}` : `then sit ${mocks}`;
    const unlock = personalizedOffer ? 'Unlock the full topic breakdown and the explanation' : 'Unlock to see which ones and read the explanation';
    return `On this sitting, ${weakCount} of your ${topicCount} topics came in under 70%. ${unlock} for each of your ${misses}, ${next}.`;
  }
  if (!missed) return '';
  return `Every topic cleared 70% on this sitting. Unlock the explanations for your ${misses}, then sit ${mocks} to check it holds on new questions.`;
}

/** Reply after the "what's stopping you?" survey: answer the objection with facts, not pressure. */
export function abandonReasonReply(reasonId, { lifetimeUsd, monthlyUsd } = {}) {
  const monthly = Number.isFinite(Number(monthlyUsd)) ? money(monthlyUsd) : '';
  const lifetime = Number.isFinite(Number(lifetimeUsd)) ? money(lifetimeUsd) : '';
  switch (reasonId) {
    case 'price':
      return monthly
        ? `If one exam is coming up soon, monthly at ${monthly} is the smaller commitment: cancel anytime and access runs to the end of the paid month. ${
            lifetime ? `Lifetime is ${lifetime} once and never renews.` : ''
          }`.trim()
        : '';
    case 'quality':
      return 'Mock 1 stays free, so you can judge the questions before you pay. Paid access adds a written explanation for every item.';
    case 'exam_far':
      return 'Lifetime access does not expire, so buying early does not use up your time. Mock 1 stays free until you are ready.';
    case 'payment':
      return `Checkout runs on Stripe and takes card, or Apple Pay / Google Pay where available. If a payment fails, email ${BILLING_EMAIL} and we will help.`;
    default:
      return '';
  }
}
